import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import {
  getOrderedQaItems,
  type QaReviewItem,
} from "@/utils/build-coverage-beta-qa-result";
import type { CoverageBetaQaReason } from "@/types/coverage";
import { cn } from "@/utils/utils";
import { isCoverageBetaReadOnlyRoute } from "@/utils/coverage-beta-read-only";
import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconSquareDashed,
  IconUser,
} from "@tabler/icons-react";
import { useState, type ComponentProps, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ProductImageThumbnail } from "../coverage-beta-right-panel/coverage-beta-summary-view/summary-row/product-image-thumbnail/ProductImageThumbnail";

/**
 * Reason chips shown for a non-deleted empty area (Out of Stock Confirmed or
 * Empty Area). `not_empty_area` / `remaining_product_facings` /
 * `blue_tag_present` / `not_main_aisle` reuse EMPTY_AREA_OPTIONS' value
 * strings (config/data.ts) — a QA reviewer flagging one is asserting the
 * original labeler's answer to that same empty-area question was wrong.
 * `product_match` has no original-question equivalent.
 */
export const AREA_QA_REASONS: { value: CoverageBetaQaReason; label: string }[] =
  [
    { value: "not_empty_area", label: "Not empty area" },
    { value: "product_match", label: "Product match" },
    { value: "remaining_product_facings", label: "Remaining facings" },
    { value: "blue_tag_present", label: "Blue tag present" },
    { value: "not_main_aisle", label: "Not main aisle" },
  ];

/** Reason chip shown for a non-deleted, active unblurred-person box. */
const PERSON_QA_REASONS: { value: CoverageBetaQaReason; label: string }[] = [
  { value: "not_unblurred_person", label: "Not an unblurred person" },
];

/** Reason chip shown for any deleted item (area or person) — asserts the delete/keep call was wrong. */
const DELETED_QA_REASONS: { value: CoverageBetaQaReason; label: string }[] = [
  { value: "incorrect_removal", label: "Incorrect removal" },
];

/** The reason chips applicable to a given reviewable item. */
const reasonOptionsFor = (item: QaReviewItem) =>
  item.isDeleted
    ? DELETED_QA_REASONS
    : item.kind === "person"
      ? PERSON_QA_REASONS
      : AREA_QA_REASONS;

/**
 * A settings-row checkbox: label on the left, control pinned to the right —
 * same alignment as the number-input row below it, so every row in
 * "Task-level corrections" reads consistently instead of alternating
 * label-left/control-right with label+control-both-left. Still one native
 * Mantine `<label>` wrapping both pieces (via `labelPosition="left"` +
 * stretched `body`), so clicking anywhere on the row toggles it — no manual
 * click-handling or double-fire risk.
 */
const RowCheckbox = (
  props: ComponentProps<typeof Checkbox> & { label: string },
) => (
  <Checkbox
    labelPosition="left"
    styles={{
      root: { width: "100%" },
      body: { width: "100%", justifyContent: "space-between" },
      labelWrapper: { flex: 1 },
    }}
    {...props}
  />
);

/** Small-caps section eyebrow — consistent header style for each card below. */
const SectionLabel = (props: { children: ReactNode }) => (
  <Text
    size="xs"
    fw={700}
    c="dimmed"
    tt="uppercase"
    style={{ letterSpacing: "0.05em" }}
  >
    {props.children}
  </Text>
);

/**
 * "Task-level corrections" card — the same fields regardless of which item
 * (if any) the pager is on, so it's defined once and rendered both from the
 * normal per-item view and from the zero-boxes empty state below.
 *
 * Collapsed by default. These are answered once per task, while everything
 * above is answered once per box, and expanded they run roughly half the
 * panel's default height — enough to push the box's own note and "Mark as
 * Correct" below the fold on every single box. "Flag for labeler review"
 * deliberately sits outside this card (see FlagLabelerRow below) so it stays
 * visible without opening anything.
 */
const TaskLevelCorrections = (props: { defaultOpen?: boolean }) => {
  const readOnlyVerdicts = isCoverageBetaReadOnlyRoute(useLocation().pathname);
  const qaTaskLevelCorrections = useCoverageBetaStore(
    (s) => s.qaTaskLevelCorrections,
  );
  const setQaTaskLevelCorrections = useCoverageBetaStore(
    (s) => s.setQaTaskLevelCorrections,
  );

  // How many of the card's own fields carry an answer — surfaced on the
  // collapsed header so it never hides content the reviewer has no reason to
  // suspect is there. `flagLabeler` is excluded: it renders outside the
  // collapse, so it's never hidden and needs no such hint.
  const filledCount = [
    qaTaskLevelCorrections.unblurredPersonsMissed,
    qaTaskLevelCorrections.boxesShouldHaveBeenDrawn > 0,
    qaTaskLevelCorrections.qaFeedback.trim() !== "",
  ].filter(Boolean).length;

  // `null` = the reviewer hasn't chosen yet, so fall back to "open iff there's
  // already something in here". That covers the admin case for free: hydration
  // lands after first paint, and a plain `useState(filledCount > 0)` would
  // have captured the pre-hydration `false` and stayed shut. Clicking pins a
  // real boolean, so a card with content can still be collapsed.
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const opened = isOpen ?? (props.defaultOpen || filledCount > 0);

  return (
    <Paper withBorder radius="md" p="sm" className="bg-gray-50">
      <UnstyledButton
        className="w-full"
        aria-expanded={opened}
        onClick={() => setIsOpen(!opened)}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconChevronRight
              size={14}
              className={cn(
                "text-gray-500 transition-transform",
                opened && "rotate-90",
              )}
            />
            <SectionLabel>Task corrections</SectionLabel>
          </Group>
          <Badge
            size="xs"
            variant="light"
            color={filledCount > 0 ? "blue" : "gray"}
          >
            {filledCount > 0 ? `${filledCount} set` : "once per task"}
          </Badge>
        </Group>
      </UnstyledButton>
      <Collapse in={opened}>
        <Stack gap="md" mt="sm">
          <RowCheckbox
            label="Unblurred persons boxes should have been drawn"
            checked={qaTaskLevelCorrections.unblurredPersonsMissed}
            disabled={readOnlyVerdicts}
            onChange={(e) =>
              setQaTaskLevelCorrections({
                unblurredPersonsMissed: e.currentTarget.checked,
              })
            }
          />
          <Group justify="space-between" wrap="nowrap" gap="md">
            <Text size="sm">Empty areas that should have been drawn</Text>
            <NumberInput
              value={qaTaskLevelCorrections.boxesShouldHaveBeenDrawn}
              onChange={(value) =>
                setQaTaskLevelCorrections({
                  boxesShouldHaveBeenDrawn: Number(value) || 0,
                })
              }
              min={0}
              disabled={readOnlyVerdicts}
              w={80}
            />
          </Group>
          <Textarea
            autosize
            minRows={2}
            label="QA Feedback"
            placeholder="Optional notes for the labeler..."
            value={qaTaskLevelCorrections.qaFeedback}
            disabled={readOnlyVerdicts}
            onChange={(e) =>
              setQaTaskLevelCorrections({
                qaFeedback: e.currentTarget.value,
              })
            }
          />
        </Stack>
      </Collapse>
    </Paper>
  );
};

/**
 * "Flag for labeler review" — a task-level field like the ones above, but
 * kept out of that collapsible card and rendered below it: it's the one
 * escalation on this panel, so it has to be visible (and one click away)
 * without the reviewer first expanding anything.
 */
const FlagLabelerRow = () => {
  const readOnlyVerdicts = isCoverageBetaReadOnlyRoute(useLocation().pathname);
  const flagLabeler = useCoverageBetaStore(
    (s) => s.qaTaskLevelCorrections.flagLabeler,
  );
  const setQaTaskLevelCorrections = useCoverageBetaStore(
    (s) => s.setQaTaskLevelCorrections,
  );

  return (
    <Paper withBorder radius="md" p="sm">
      <RowCheckbox
        label="Flag task for labeler review"
        checked={flagLabeler}
        disabled={readOnlyVerdicts}
        onChange={(e) =>
          setQaTaskLevelCorrections({ flagLabeler: e.currentTarget.checked })
        }
      />
    </Paper>
  );
};

/**
 * The Coverage Beta QA "Box Review Panel" (see the Figma linked in
 * lebron/docs/plans/coverage-beta-qa.md) — replaces the normal edit-mode
 * footer content when the store is in QA mode. Renders instead of
 * `CoverageBetaRightFooter`'s usual content; the canvas/sidebar/planogram
 * chrome around it is unchanged, hydrated read-only same as admin-browse.
 *
 * Note: the app's `#root` has a global `text-align: center` (index.css, a
 * leftover Vite default) that every plain-text block in this panel would
 * otherwise inherit — including Mantine's own Checkbox/Textarea label
 * elements, which have no text-align of their own. Rather than overriding it
 * on each Text/label individually (easy to miss one — see git history on this
 * file), the panel's own root sets `text-align: left` once, so the whole
 * subtree inherits the correct value instead of the page's.
 */
export const CoverageBetaQaPanel = () => {
  // Same read-only route detection CoverageBetaHeader uses — an admin browsing
  // a completed QA task, or the labeler reading its feedback, sees verdicts but
  // can't edit them.
  const readOnlyVerdicts = isCoverageBetaReadOnlyRoute(useLocation().pathname);
  const idxToFeEmptyArea = useCoverageBetaStore((s) => s.idxToFeEmptyArea);
  const persons = useCoverageBetaStore((s) => s.persons);
  const activeQaItemKey = useCoverageBetaStore((s) => s.activeQaItemKey);
  const setActiveQaItemKey = useCoverageBetaStore((s) => s.setActiveQaItemKey);
  const qaVerdicts = useCoverageBetaStore((s) => s.qaVerdicts);
  const setQaBoxReasons = useCoverageBetaStore((s) => s.setQaBoxReasons);
  const qaBoxNotes = useCoverageBetaStore((s) => s.qaBoxNotes);
  const setQaBoxNote = useCoverageBetaStore((s) => s.setQaBoxNote);

  const items = getOrderedQaItems(idxToFeEmptyArea, persons);
  const total = items.length;
  // Falls back to the first item both before the reviewer has navigated at
  // all (activeQaItemKey starts null) and if it ever points at something no
  // longer in the list.
  const position = Math.max(
    0,
    items.findIndex((i) => i.key === activeQaItemKey),
  );
  const activeItem = items[position];

  // A submission with no detections and nothing hand-drawn has nothing to
  // page through — `activeItem` is never set (activeQaItemKey stays null;
  // see CoverageBetaTask's QA hydration effect). Still render the task-level
  // corrections card and let the task be reviewed/submitted as-is, rather
  // than leaving the whole panel blank with no way to confirm it.
  if (!activeItem) {
    return (
      <div
        className="h-full flex flex-col gap-4 p-4 overflow-y-auto"
        style={{ textAlign: "left" }}
      >
        <div className="flex flex-col items-center justify-center gap-1 py-6 text-center">
          <Text size="sm" fw={600} c="dimmed">
            No areas labeled
          </Text>
          <Text size="xs" c="dimmed">
            This task had no detections to review.
          </Text>
        </div>
        <TaskLevelCorrections defaultOpen />
        <FlagLabelerRow />
      </div>
    );
  }

  const area = activeItem.kind === "area" ? activeItem.area : undefined;
  const products = area?.selectedProducts ?? [];
  // undefined = not yet reviewed at all; [] = explicitly confirmed correct;
  // non-empty = flagged incorrect. Only presence in qaVerdicts counts as
  // "reviewed" — see handleMarkCorrect below, the only place that ever writes
  // an empty array.
  const reasons = qaVerdicts[activeItem.key];

  const goTo = (delta: 1 | -1) => {
    const next = items[position + delta];
    if (next) setActiveQaItemKey(next.key);
  };

  // The only way an item gets recorded as "no issues" — no more implicit
  // default-on-navigate. Auto-advances so throughput doesn't regress versus
  // the old combined "Confirm & Next" button.
  const handleMarkCorrect = () => {
    setQaBoxReasons(activeItem.key, []);
    goTo(1);
  };

  let activeItemLabel: string;
  if (activeItem.kind === "person") {
    activeItemLabel = activeItem.isDeleted
      ? "Deleted Person"
      : "Unblurred Person";
  } else {
    activeItemLabel = activeItem.isDeleted ? "Deleted Area" : "Empty Area";
  }

  // null when the "Matched products" list below already carries the name(s) —
  // no point repeating the first one here as a title.
  let activeItemTitle: string | null;
  if (activeItem.kind === "person") {
    activeItemTitle = "Unblurred person box";
  } else if (products.length > 0) {
    activeItemTitle = null;
  } else {
    activeItemTitle = activeItem.isDeleted
      ? "Deleted detection (no product)"
      : "Empty area (no match)";
  }

  let itemMeta = `${activeItemLabel} · item ${position + 1}`;
  if (products.length > 0) {
    itemMeta += ` · ${products.length} product${products.length === 1 ? "" : "s"}`;
  }

  let headerTile: ReactNode;
  if (activeItem.kind === "person") {
    headerTile = (
      <div className="size-8 shrink-0 rounded border border-red-200 bg-red-50 flex items-center justify-center">
        <IconUser size={16} className="text-red-500" />
      </div>
    );
  } else {
    headerTile = (
      <div className="size-8 shrink-0 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
        <IconSquareDashed size={16} className="text-gray-500" />
      </div>
    );
  }

  let verdictStatus: ReactNode;
  if (reasons === undefined) {
    verdictStatus = readOnlyVerdicts ? (
      <Text size="sm" c="dimmed" fs="italic">
        Not reviewed
      </Text>
    ) : (
      <Button
        fullWidth
        color="green"
        leftSection={<IconCheck size={16} />}
        onClick={handleMarkCorrect}
      >
        Mark as Correct
      </Button>
    );
  } else if (reasons.length === 0) {
    verdictStatus = (
      <Group
        gap="xs"
        wrap="nowrap"
        className="rounded-md bg-green-50 border border-green-200 px-3 py-2"
      >
        <IconCircleCheck size={18} className="text-green-600 shrink-0" />
        <Text size="sm" fw={600} c="green.8">
          Reviewed — no issues
        </Text>
      </Group>
    );
  } else {
    verdictStatus = (
      <Group
        gap="xs"
        wrap="nowrap"
        className="rounded-md bg-red-50 border border-red-200 px-3 py-2"
      >
        <IconAlertTriangle size={18} className="text-red-600 shrink-0" />
        <Text size="sm" fw={600} c="red.8">
          Flagged incorrect ({reasons.length})
        </Text>
      </Group>
    );
  }

  return (
    <div
      className="h-full flex flex-col gap-4 p-4 overflow-y-auto"
      style={{ textAlign: "left" }}
    >
      {/* Item header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        {headerTile}
        <div className="flex-1 min-w-0">
          <Text size="xs" c="dimmed">
            {itemMeta}
          </Text>
          {activeItemTitle !== null && (
            <Text size="sm" fw={600} truncate>
              {activeItemTitle}
            </Text>
          )}
        </div>
        <Group gap={2} className="shrink-0 bg-gray-100 rounded-full px-1 py-1">
          <ActionIcon
            variant="subtle"
            color="gray"
            radius="xl"
            size="sm"
            disabled={position === 0}
            onClick={() => goTo(-1)}
          >
            <IconChevronLeft size={14} />
          </ActionIcon>
          <Text
            size="xs"
            fw={600}
            c="dimmed"
            px={4}
            style={{ whiteSpace: "nowrap" }}
          >
            {position + 1} / {total}
          </Text>
          <ActionIcon
            variant="subtle"
            color="gray"
            radius="xl"
            size="sm"
            disabled={position === total - 1}
            onClick={() => goTo(1)}
          >
            <IconChevronRight size={14} />
          </ActionIcon>
        </Group>
      </div>

      {/* Matched products — every product the labeler selected for this area,
          not just the first, so a reviewer can actually judge the OOS
          confirmation when more than one was picked. */}
      {products.length > 0 && (
        <Stack gap="xs">
          <SectionLabel>Matched products</SectionLabel>
          {products.map((p, i) => (
            <Group
              key={`${p.id ?? "na"}-${i}`}
              gap="sm"
              wrap="nowrap"
              align="flex-start"
            >
              <ProductImageThumbnail product={p} />
              <Text size="sm" className="flex-1 min-w-0">
                {p.description || p.identifier || "Unnamed product"}
              </Text>
            </Group>
          ))}
        </Stack>
      )}

      {/* Per-box reasons */}
      <Stack gap="sm">
        <SectionLabel>Mark all incorrect decisions</SectionLabel>
        <Chip.Group
          multiple
          value={reasons ?? []}
          onChange={(value) => {
            const nextReasons = value as CoverageBetaQaReason[];
            // Deselecting the last chip must NOT count as "marked correct" —
            // that's the "Mark as Correct" button's job specifically. Go
            // back to unreviewed instead, so the reviewer has to explicitly
            // confirm the box again.
            setQaBoxReasons(
              activeItem.key,
              nextReasons.length === 0 ? undefined : nextReasons,
            );
          }}
        >
          <Group gap="xs">
            {reasonOptionsFor(activeItem).map(({ value, label }) => (
              <Chip
                key={value}
                value={value}
                color="red"
                radius="md"
                disabled={readOnlyVerdicts}
              >
                {label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>

        {/* The detail the fixed reason vocabulary can't carry — always shown,
            and always optional: it's deliberately not part of what
            `isQaReadyToSubmit` gates on. Keyed by QaItemKey in its own store
            slice, so it survives paging away, "Mark as Correct" auto-advancing,
            and clearing the last reason chip (which resets this box's
            verdict). */}
        <SectionLabel>Note for this box</SectionLabel>
        <Textarea
          autosize
          minRows={2}
          maxRows={4}
          placeholder="Optional — what's wrong, or anything the labeler should know"
          value={qaBoxNotes[activeItem.key] ?? ""}
          disabled={readOnlyVerdicts}
          onChange={(e) => setQaBoxNote(activeItem.key, e.currentTarget.value)}
        />

        {verdictStatus}
      </Stack>

      {/* Task-level corrections */}
      <TaskLevelCorrections />
      <FlagLabelerRow />
    </div>
  );
};
